import { useCallback, useEffect, useRef } from 'react';
import {
  buildOauthDenyUrl,
  isRedirectForRequest,
  OauthAuthorizeRequest,
  oauthConsentPath,
  signInPathFor,
} from 'modules/auth/domain/utils/oauth-authorize-request';
import { authCheck, authSignOut } from 'modules/auth/store/actions';
import { useNavigate } from 'react-router-dom';
import { useGrantOauthConsentMutation } from 'modules/auth/store/oauth-api';
import { useAppDispatch } from 'store/hooks';
import { leaveToUrl } from '../utils/leave-to-url';
import { useAuth } from './use-auth';

/** Адрес с кодом пришёл не на `redirect_uri` запроса — переход не делаем. */
export class OauthRedirectMismatchError extends Error {
  constructor() {
    super('OAuth redirect_url does not match redirect_uri');
  }
}

/**
 * Согласие на доступ AI-клиента. Экран живёт вне `SecureRoute`: тот без входа теряет адрес,
 * а без организации уводит в настройки — согласию организация не нужна.
 *
 * Сессию проверяет сам: со входа пользователь уже в сторе, а из AI-клиента приходит в новой
 * вкладке — тогда `authCheck` с этим же адресом (нет входа — `/sign-in` с теми же параметрами).
 */
export const useOauthConsent = (request: OauthAuthorizeRequest | null) => {
  const dispatch = useAppDispatch();
  const { user, isAuthed, isChecking } = useAuth();
  const [grantConsent] = useGrantOauthConsentMutation();
  const navigate = useNavigate();

  // Пользователя нет — проверяем сессию, один раз за открытие экрана. Не по `isChecked`: после
  // проверки без входа или «Войти под другим аккаунтом» он уже `true`, а `user` пуст, и «Назад»
  // со страницы входа оставлял бы вечное «Проверяем вход…». Без входа `authCheck` сам уведёт на `/sign-in`.
  const isCheckStarted = useRef(false);
  useEffect(() => {
    if (!request || user || isChecking || isCheckStarted.current) return;
    isCheckStarted.current = true;
    dispatch(authCheck(oauthConsentPath(request.search)));
  }, [dispatch, request, user, isChecking]);

  /** «Разрешить»: код от бэкенда — и браузер уходит к клиенту. */
  const allow = useCallback(async () => {
    if (!request) return;
    const redirectUrl = await grantConsent(request.search).unwrap();
    if (!isRedirectForRequest(redirectUrl, request)) throw new OauthRedirectMismatchError();
    leaveToUrl(redirectUrl);
  }, [grantConsent, request]);

  /** «Отказать»: клиенту `error=access_denied` с исходным `state`. */
  const deny = useCallback(() => {
    if (request) leaveToUrl(buildOauthDenyUrl(request));
  }, [request]);

  /**
   * «Войти под другим аккаунтом»: бэкенд выдаёт код только владельцу `client_id`, чужому отвечает 403.
   * Выход и вход с теми же параметрами — после входа снова сюда.
   */
  const switchAccount = useCallback(() => {
    if (!request) return;
    dispatch(authSignOut());
    navigate(signInPathFor(oauthConsentPath(request.search)), { replace: true });
  }, [dispatch, navigate, request]);

  return { user: isAuthed ? user : null, allow, deny, switchAccount };
};
